import { useState } from 'react';
import { Plus, Search, Upload, Download, AlertTriangle, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ClienteRow } from '@/components/clientes/ClienteRow';
import { ClienteRowIncompleto } from '@/components/clientes/ClienteRowIncompleto';
import { ClienteModal } from '@/components/clientes/ClienteModal';
import { ImportModal } from '@/components/clientes/ImportModal';
import { exportCustomersToExcel, Customer as ExportCustomer, CollectionRule } from '@/lib/exportExcel';

export interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  notes: string | null;
  cluster: 'bom_pagador' | 'regular' | 'mau_pagador' | null;
  whatsapp: string | null;
  collection_rule_id: string | null;
  created_at: string;
  protest_status: string | null;
}

interface Invoice {
  id: string;
  customer_id: string;
  amount: number;
  status: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

const Clientes = () => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const { data: customers = [], isLoading, refetch } = useQuery({
    queryKey: ['customers', search],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      let query = supabase
        .from('customers')
        .select('id, name, tax_id, email, phone, address, city, state, postal_code, notes, cluster, whatsapp, collection_rule_id, created_at, protest_status')
        .eq('company_id', profile.company_id)
        .order('name');

      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,tax_id.ilike.%${search}%`);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;
      return data as Customer[];
    }
  });

  // Fetch collection rules for export
  const { data: collectionRules = [] } = useQuery({
    queryKey: ['collection-rules-export'],
    queryFn: async () => {
      const { data } = await supabase
        .from('collection_rules')
        .select('id, name');
      return (data || []) as CollectionRule[];
    }
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ['all-invoices-summary'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('invoices')
        .select('id, customer_id, amount, status')
        .eq('company_id', profile.company_id)
        .in('status', ['pending', 'overdue']);

      if (error) throw error;
      return data as Invoice[];
    }
  });

  // Calculate KPIs
  const customerInvoices = invoices.reduce((acc, inv) => {
    if (!acc[inv.customer_id]) acc[inv.customer_id] = { pending: 0, overdue: 0 };
    if (inv.status === 'pending') acc[inv.customer_id].pending += inv.amount;
    if (inv.status === 'overdue') acc[inv.customer_id].overdue += inv.amount;
    return acc;
  }, {} as Record<string, { pending: number; overdue: number }>);

  const inadimplentes = customers.filter(c => c.cluster === 'mau_pagador' || customerInvoices[c.id]?.overdue > 0);
  // Cadastro incompleto: missing email, OR missing both phone channels, OR missing address
  const cadastroIncompleto = customers.filter(c => 
    !c.email || (!c.phone && !c.whatsapp) || !c.address
  );

  const totalEmAberto = invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);
  const totalVencido = invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);
  
  // Calculate protest/negativado values
  const protestadoCustomerIds = customers.filter(c => c.protest_status === 'protestado').map(c => c.id);
  const negativadoCustomerIds = customers.filter(c => c.protest_status === 'negativado').map(c => c.id);
  
  const totalProtestado = invoices
    .filter(i => i.status === 'overdue' && protestadoCustomerIds.includes(i.customer_id))
    .reduce((sum, i) => sum + i.amount, 0);
  const totalNegativado = invoices
    .filter(i => i.status === 'overdue' && negativadoCustomerIds.includes(i.customer_id))
    .reduce((sum, i) => sum + i.amount, 0);

  const handleExportAll = () => {
    const toExport = filteredCustomers as ExportCustomer[];
    exportCustomersToExcel(toExport, collectionRules, 'clientes');
  };

  const handleExportIncomplete = () => {
    const toExport = cadastroIncompleto as ExportCustomer[];
    exportCustomersToExcel(toExport, collectionRules, 'clientes_cadastro_incompleto');
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedCustomer(null);
    setModalOpen(true);
  };

  const filteredCustomers = activeTab === 'all' 
    ? customers 
    : activeTab === 'inadimplente'
      ? inadimplentes
      : cadastroIncompleto;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">Gerencie seus clientes e contatos</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2 hover-pop"
            onClick={() => setImportModalOpen(true)}
          >
            <Upload className="h-4 w-4" />
            Importar
          </Button>
          <Button className="gap-2 hover-pop" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            Visão geral
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-muted text-xs">
              {customers.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="inadimplente" className="gap-2">
            Inadimplente
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))] text-xs">
              {inadimplentes.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="incompleto" className="gap-2">
            Cadastro incompleto
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-muted text-xs">
              {cadastroIncompleto.length}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Inadimplente KPIs */}
        <TabsContent value="inadimplente" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-card rounded-xl border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Valor em aberto</p>
              <p className="text-lg font-bold text-[hsl(var(--basic-900))]">{formatCurrency(totalEmAberto)}</p>
            </div>
            <div className="bg-[hsl(var(--careful-50))] rounded-xl border border-[hsl(var(--careful-200))] p-4">
              <p className="text-xs text-[hsl(var(--careful-600))] uppercase tracking-wider">Valor vencido</p>
              <p className="text-lg font-bold text-[hsl(var(--careful-600))]">{formatCurrency(totalVencido)}</p>
            </div>
            <div className="bg-card rounded-xl border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Protestado</p>
              <p className="text-lg font-bold text-[hsl(var(--basic-900))]">{formatCurrency(totalProtestado)}</p>
            </div>
            <div className="bg-card rounded-xl border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Negativado</p>
              <p className="text-lg font-bold text-[hsl(var(--basic-900))]">{formatCurrency(totalNegativado)}</p>
            </div>
            <div className="bg-card rounded-xl border p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Clientes inadimpl.</p>
              <p className="text-lg font-bold text-[hsl(var(--basic-900))]">
                {Math.round((inadimplentes.length / customers.length) * 100) || 0}%
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  ({inadimplentes.length}/{customers.length})
                </span>
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Cadastro Incompleto Alert */}
        <TabsContent value="incompleto" className="mt-6">
          <div className="bg-[hsl(var(--basic-50))] border border-[hsl(var(--basic-200))] rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--basic-500))] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-bold text-[hsl(var(--basic-800))]">
                  {cadastroIncompleto.length} clientes com cadastro incompleto
                </p>
                <p className="text-sm text-[hsl(var(--basic-600))] mt-1">
                  Complete o cadastro (email, telefone, endereço) para melhorar a taxa de recuperação das cobranças.
                </p>
              </div>
            <div className="flex gap-2 shrink-0">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={handleExportIncomplete}
              >
                <Download className="h-4 w-4" />
                Baixar XLS
              </Button>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>

    {/* Search and Filters */}
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
      <div className="relative max-w-md flex-1">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou CPF/CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Total de clientes: <strong className="text-foreground">{filteredCustomers.length}</strong></span>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2"
          onClick={handleExportAll}
        >
          <Download className="h-4 w-4" />
          XLS
        </Button>
      </div>
    </div>

      {/* Lista de Clientes */}
      <div className="bg-card rounded-lg border overflow-hidden">
        {/* Header - dynamic based on tab */}
        {activeTab === 'incompleto' ? (
          <div className="grid grid-cols-[2fr_1fr_6rem_6rem_6rem_8rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
            <span>Nome</span>
            <span>Documento</span>
            <span className="text-center">Email</span>
            <span className="text-center">WhatsApp</span>
            <span className="text-center">Endereço</span>
            <span className="text-right">Ações</span>
          </div>
        ) : (
          <div className="grid grid-cols-[2fr_1fr_8rem_1fr_8rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
            <span>Nome</span>
            <span>Documento</span>
            <span className="text-center">Situação</span>
            <span>Régua</span>
            <span className="text-right">Ações</span>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            Carregando...
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground flex flex-col items-center gap-2">
            <UserX className="h-10 w-10 text-muted-foreground/50" />
            <p>{search ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado.'}</p>
            {!search && (
              <Button variant="link" onClick={handleCreate}>
                Clique aqui para cadastrar o primeiro cliente
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {activeTab === 'incompleto' ? (
              filteredCustomers.map((customer) => (
                <ClienteRowIncompleto 
                  key={customer.id} 
                  customer={customer}
                  onEdit={() => handleEdit(customer)}
                  onDeleted={() => refetch()}
                />
              ))
            ) : (
              filteredCustomers.map((customer) => (
                <ClienteRow 
                  key={customer.id} 
                  customer={customer}
                  onEdit={() => handleEdit(customer)}
                  onDeleted={() => refetch()}
                />
              ))
            )}
          </div>
        )}
      </div>

      <ClienteModal 
        open={modalOpen}
        onOpenChange={setModalOpen}
        customer={selectedCustomer}
        onSaved={() => refetch()}
      />

      <ImportModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
      />
    </div>
  );
};

export default Clientes;
